// Summer Volleyball dual-team flyer.
// Restyled to match the Pitt Gabruz Cricket bespoke flyer:
//   - Red #dc2626 + black ground + white type
//   - Bold italic Anton display, Inter body
//   - Diagonal red slashes top-left + bottom-right
//   - Centered header with Gabruz Universe brand + league line
//   - Match hero with PITT GABRUZ vs PITT BAAZIGARS
//   - Compact match-info strip (date · report · court)
//   - Two roster blocks (one per sub-team) using the same jersey-row style
//   - Volunteers ribbon just above the striped reporting foot

import React from "react";
import { Calendar, Clock, MapPin } from "lucide-react";
import { formatDateTemplate, formatTimeTemplate, splitVenue } from "./flyerHelpers";

function TeamRoster({ players }) {
  return (
    <div className="pgc-bash-vb-team">
      <ol className="pgc-bash-xi pgc-bash-vb-list">
        {players.map((p, i) => (
          <li key={p.id || i} className="pgc-bash-row">
            <span className="pgc-bash-row-bar"/>
            <span className="pgc-bash-num">{String(i + 1).padStart(2, "0")}</span>
            <div className="pgc-bash-row-body">
              <strong>{p.name}</strong>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function SummerVolleyballFlyer({
  match, reportingTime, venue,
  startingPlayers, benchPlayers,
  teamBStartingPlayers, teamBBenchPlayers,
  rules,
  snacksVolunteer, alcoholVolunteer
}) {
  const { short: venueShort } = splitVenue(venue);
  const showVolunteers = !!(snacksVolunteer || alcoholVolunteer);

  return (
    <div className="pgc-bash pgc-bash--vb">
      {/* Watermark logo (behind content) */}
      <img
        className="pgc-bash-watermark"
        src="/logo.png"
        alt=""
        aria-hidden="true"
        crossOrigin="anonymous"
        onError={e => e.currentTarget.style.display = "none"}
      />

      {/* Decorative red energy slashes */}
      <div className="pgc-bash-slash pgc-bash-slash--tl"/>
      <div className="pgc-bash-slash pgc-bash-slash--br"/>

      {/* HEADER */}
      <header className="pgc-bash-head">
        <div className="pgc-bash-head-text">
          <p className="pgc-bash-league">
            <span/> SUMMER VOLLEYBALL CHAMPIONSHIP <span/>
          </p>
          <h1 className="pgc-bash-brand">GABRUZ UNIVERSE</h1>
          <span className="pgc-bash-brand-underline" aria-hidden="true"/>
        </div>
      </header>

      {/* MATCH HERO — Pitt Gabruz vs Pitt Baazigars */}
      <section className="pgc-bash-hero">
        <div className="pgc-bash-team pgc-bash-team--home">
          <small>TEAM A</small>
          <strong>PITT GABRUZ</strong>
        </div>
        <div className="pgc-bash-vs">
          <span className="pgc-bash-vs-line"/>
          <em>VS</em>
          <span className="pgc-bash-vs-line"/>
        </div>
        <div className="pgc-bash-team pgc-bash-team--away">
          <small>TEAM B</small>
          <strong>PITT BAAZIGARS</strong>
        </div>
      </section>

      {/* MATCH INFO STRIP */}
      <div className="pgc-bash-info">
        <div className="pgc-bash-info-cell">
          <Calendar size={13}/>
          <small>DATE</small>
          <b>{formatDateTemplate(match?.date)}</b>
        </div>
        <div className="pgc-bash-info-cell">
          <Clock size={13}/>
          <small>REPORT BY</small>
          <b>{formatTimeTemplate(reportingTime)}</b>
        </div>
        <div className="pgc-bash-info-cell">
          <MapPin size={13}/>
          <small>COURT</small>
          <b>{venueShort}</b>
        </div>
      </div>

      {/* TWO ROSTERS — hero already labels which column belongs to which team. */}
      <div className="pgc-bash-vb-rosters">
        <TeamRoster players={startingPlayers}/>
        <TeamRoster players={teamBStartingPlayers}/>
      </div>

      {/* VOLUNTEERS RIBBON */}
      {showVolunteers && (
        <div className="pgc-bash-vb-volunteers">
          <div className="pgc-bash-vb-volunteers-slot">
            <small>SNACKS</small>
            <b>{snacksVolunteer || "— TBD —"}</b>
          </div>
          <span className="pgc-bash-vb-volunteers-divider" aria-hidden="true"/>
          <div className="pgc-bash-vb-volunteers-slot">
            <small>BEVERAGES</small>
            <b>{alcoholVolunteer || "— TBD —"}</b>
          </div>
        </div>
      )}

      {/* STRIPED FOOTER — same as PGC-Cricket */}
      <footer className="pgc-bash-foot">
        <div className="pgc-bash-foot-stripes"/>
        <div className="pgc-bash-foot-text">
          <small>REPORT BY</small>
          <strong>{formatTimeTemplate(reportingTime)}</strong>
        </div>
      </footer>
    </div>
  );
}
